import { getMarkRange, type Range } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { useEditorDom } from '../../lib/editorDom';

/** Was entfernt wird: eine Nennung (verknüpftes Wort bzw. Passage) oder ein allein stehender Verweis `[@schlüssel]` */
export type RemovableRef = { kind: 'link'; range: Range } | { kind: 'token'; range: Range; key: string };

interface Shown {
  ref: RemovableRef;
  left: number;
  top: number;
}

/** Bereich der Verknüpfung bzw. des Verweises unter dem Element (null: keiner) */
function refAt(editor: Editor, el: HTMLElement): RemovableRef | null {
  const view = editor.view;
  const link = el.closest<HTMLElement>('.rt-ref-link');
  if (link) {
    const pos = view.posAtDOM(link, 0);
    const $pos = editor.state.doc.resolve(pos);
    const type = editor.schema.marks.refLink!;
    const mark = $pos.parent.childAfter($pos.parentOffset).node?.marks.find((m) => m.type === type);
    const range = mark ? getMarkRange($pos, type, mark.attrs) : undefined;
    return range ? { kind: 'link', range } : null;
  }
  const token = el.closest<HTMLElement>('.rt-ref[data-ref]');
  if (token) {
    const from = view.posAtDOM(token, 0);
    return { kind: 'token', range: { from, to: from + (token.textContent ?? '').length }, key: token.getAttribute('data-ref') ?? '' };
  }
  return null;
}

/**
 * Verweise entfernen: Fährt man über ein verknüpftes Wort (bzw. einen Verweis „[@schlüssel]“), erscheint an seinem Ende ein kleines ✕.
 * Es löst die Verknüpfung – das Wort bleibt, ein allein stehender Verweis wird zum Namen des Elements. Rückgängig mit Strg+Z.
 */
export function RefRemover({ editor, onRemove }: { editor: Editor; onRemove: (ref: RemovableRef) => void }) {
  const layer = useRef<HTMLDivElement>(null);
  const hide = useRef<number | undefined>(undefined);
  const [shown, setShown] = useState<Shown | null>(null);
  const dom = useEditorDom(editor);

  useEffect(() => {
    if (!dom) return;
    const later = (): void => {
      window.clearTimeout(hide.current);
      hide.current = window.setTimeout(() => setShown(null), 220);
    };
    const over = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      const ref = target && !editor.isDestroyed ? refAt(editor, target) : null;
      if (!ref || !layer.current) return;
      window.clearTimeout(hide.current);
      const box = layer.current.getBoundingClientRect();
      const end = editor.view.coordsAtPos(ref.range.to, -1);
      setShown({ ref, left: end.right - box.left, top: end.top - box.top });
    };
    const out = (e: MouseEvent): void => {
      if ((e.target as HTMLElement | null)?.closest?.('.rt-ref-link, .rt-ref[data-ref]')) later();
    };
    // nach einer Änderung stimmen die Lagen nicht mehr
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }): void => {
      if (transaction.docChanged) setShown(null);
    };
    dom.addEventListener('mouseover', over);
    dom.addEventListener('mouseout', out);
    editor.on('transaction', onTransaction);
    return () => {
      dom.removeEventListener('mouseover', over);
      dom.removeEventListener('mouseout', out);
      editor.off('transaction', onTransaction);
      window.clearTimeout(hide.current);
    };
  }, [editor, dom]);

  const label = shown?.ref.kind === 'token' ? de.editor.removeRef : de.editor.removeLink;
  return (
    <div ref={layer} className="rt-remover-layer">
      {shown && (
        <button
          type="button"
          className="rt-ref-remove"
          style={{ left: shown.left - 5, top: shown.top - 5 }}
          title={label}
          aria-label={label}
          onMouseEnter={() => window.clearTimeout(hide.current)}
          onMouseLeave={() => {
            hide.current = window.setTimeout(() => setShown(null), 220);
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onRemove(shown.ref);
            setShown(null);
          }}
          data-testid="ref-remove"
        >
          ✕
        </button>
      )}
    </div>
  );
}
